import type { SlideElement, SubQuiz } from "@ai-teach/types";

/**
 * Converts between a standalone `type:"other"` cloze slide's `elements` and an inline `SubQuiz`.
 *
 * An "other" exercise slide keeps its question in the freeform canvas `elements` (a `text` prompt
 * with a "______" blank + `textChoice` options, exactly one `isCorrectAnswer:true`) — see the API's
 * `buildClozeSlide` (apps/b2b-api/src/domains/pdfImport/clozeExercise.ts) for the canonical layout.
 * A `SubQuiz` is the flattened `{ question, options, correctAnswer }` shape. These helpers are the
 * bridge used by the "merge slides into a master's subQuizzes" (list page) and "eject a subQuiz back
 * into a slide" (editor) flows.
 *
 * The round-trip is intentionally LOSSY: an arbitrary "other" slide (images, drawings, multiple text
 * boxes) is compressed to four text fields on extract, and rebuilt from scratch on eject — so an
 * ejected slide reproduces the cloze layout, not the original elements.
 */

// ── Extract: elements → SubQuiz ──

/**
 * Flattens an "other" slide's elements into a SubQuiz. The first `text` element becomes the question;
 * `textChoice` elements become the answer set (the correct one → `correctAnswer`, the rest →
 * `options`). With no choices it degrades to a `fill_in_blank` question. Never throws — a non-cloze
 * slide simply yields empty fields for the author to fill in.
 */
export function elementsToSubQuiz(elements: SlideElement[]): SubQuiz {
  const textElement = elements.find((el) => el.type === "text");
  const choices = elements.filter((el) => el.type === "textChoice");
  const correct = choices.find((c) => c.isCorrectAnswer);
  const distractors = choices.filter((c) => !c.isCorrectAnswer);

  return {
    id: crypto.randomUUID(),
    question: textElement?.content ?? "",
    questionMode: choices.length > 0 ? "multiple_choice" : "fill_in_blank",
    options: distractors.map((d) => d.text),
    correctAnswer: correct?.text ?? "",
  };
}

// ── Rebuild: SubQuiz → elements ──
// Layout/styling constants replicate the sample cloze slide (kept in sync with clozeExercise.ts so an
// ejected slide looks the same as an AI-generated one).

const CHOICE_TEXT_STYLE = {
  color: "#000000",
  textAlign: "center",
  fontFamily: "Comic Sans MS",
  fontStyle: "none",
  textDecoration: "none",
} as const;

const NO_BORDER = {
  lineStyle: "none",
  strokeWidth: 3,
  stroke: "#000000",
  cornerRadius: 0,
} as const;

// Choices are stacked vertically at these y positions (from the sample slide).
const CHOICE_Y = [41.125, 52.75, 63.375];

function textPromptElement(content: string): SlideElement {
  return {
    id: crypto.randomUUID(),
    type: "text",
    position: { x: 6.25, y: 14.5 },
    size: { width: 88.33333333333333, height: 23.125 },
    zIndex: 0,
    border: NO_BORDER,
    backgroundColor: null,
    content,
    style: { ...CHOICE_TEXT_STYLE, fontSize: 40, fontWeight: "700" },
  };
}

function textChoiceElement(text: string, isCorrectAnswer: boolean, index: number): SlideElement {
  return {
    id: crypto.randomUUID(),
    type: "textChoice",
    position: { x: 12.708333333333332, y: CHOICE_Y[index] ?? 63.375 + (index - 2) * 11.6 },
    size: { width: 74.79166666666667, height: 7.5 },
    zIndex: 0,
    border: { ...NO_BORDER, cornerRadius: 13 },
    backgroundColor: "#FFFFFF",
    text,
    isCorrectAnswer,
    style: { ...CHOICE_TEXT_STYLE, fontSize: 23, fontWeight: "normal" },
  };
}

// Fisher-Yates shuffle so the correct answer isn't always in the same slot.
function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

/**
 * Rebuilds an "other" slide's `elements` from a SubQuiz: a text prompt plus (for multiple choice) the
 * correct answer + distractors as shuffled `textChoice`s. A `fill_in_blank` quiz yields the prompt
 * alone. Mirrors `buildClozeSlide`'s element assembly, minus the LLM.
 */
export function subQuizToElements(quiz: SubQuiz): SlideElement[] {
  const prompt = textPromptElement(quiz.question);

  if (quiz.questionMode !== "multiple_choice") {
    return [prompt];
  }

  const choices = shuffle([
    { text: quiz.correctAnswer, isCorrectAnswer: true },
    ...quiz.options.map((text) => ({ text, isCorrectAnswer: false })),
  ]);

  return [prompt, ...choices.map((c, i) => textChoiceElement(c.text, c.isCorrectAnswer, i))];
}
